import { useEffect, useState } from 'react';
import { scrollToId } from '../lib/scroll';
import { site } from '../content';

const LINKS = [
  // `watch` is what marks the link current; the three ventures are one section
  // as far as the reader is concerned.
  { id: 'bloom', label: 'Work', watch: ['bloom', 'oneisland', 'divelogic'] },
  { id: 'edge', label: 'Edge', watch: ['edge'] },
  { id: 'practice', label: 'Practice', watch: ['practice', 'record'] },
  { id: 'contact', label: 'Contact', watch: ['contact'] },
];

export function Nav() {
  const [stuck, setStuck] = useState(false);
  const [current, setCurrent] = useState<string | null>(null);

  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const seen = new Map<string, number>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) seen.set(e.target.id, e.intersectionRatio);
        let best: string | null = null;
        let bestR = 0.04;
        for (const l of LINKS) {
          const r = Math.max(...l.watch.map((w) => seen.get(w) ?? 0));
          if (r > bestR) {
            bestR = r;
            best = l.id;
          }
        }
        setCurrent(best);
      },
      { threshold: [0, 0.05, 0.25, 0.5, 0.75, 1], rootMargin: '-15% 0px -35% 0px' },
    );
    for (const id of LINKS.flatMap((l) => l.watch)) {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    }
    return () => {
      window.removeEventListener('scroll', onScroll);
      io.disconnect();
    };
  }, []);

  const go = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    scrollToId(id);
    history.replaceState(null, '', `#${id}`);
  };

  return (
    <header className="nav" data-stuck={stuck}>
      <div className="wrap nav-inner">
        <a
          className="nav-mark"
          href="#top"
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey) return;
            e.preventDefault();
            scrollToId('top');
          }}
        >
          <Mark />
          <span>{site.name}</span>
        </a>
        <nav aria-label="Sections">
          <ul className="nav-links">
            {LINKS.map((l) => (
              <li key={l.id} className={l.id === 'contact' ? 'nav-cta' : undefined}>
                <a
                  href={`#${l.id}`}
                  aria-current={current === l.id ? 'true' : undefined}
                  onClick={(e) => go(e, l.id)}
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </header>
  );
}

/**
 * The site mark: the same seed-and-branches figure as the favicon, inlined so
 * it inherits colour from the link rather than shipping a second request.
 */
function Mark() {
  return (
    <svg className="nav-glyph" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <g
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        fill="none"
      >
        <path d="M16 26 L16 15" />
        <path d="M16 17 L9 10" />
        <path d="M16 19 L23 12" />
        <path d="M9 10 L6 6" />
        <path d="M9 10 L5 11" />
        <path d="M23 12 L27 9" />
        <path d="M23 12 L25 16" />
      </g>
    </svg>
  );
}
